// @flow
import forge from 'node-forge'
import {push} from 'react-router-redux'
import * as authenticateAPI from 'api/authenticate'
import {actionTypes} from './constants'
import {webURL} from 'modules/Common/constants'
import {actionCreator} from 'modules/Common/utils/index'

import type {Action} from 'modules/Common/types'
import type {ThunkAction} from 'modules/Common/types'
import type {DeviceInfo} from 'modules/Authenticate/types'

export const login = (
  name: string,
  password: string,
  company: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  dispatch({
    type: actionTypes.LOGIN_REQUEST,
    payload: {name, password, company}
  })

  let params = {
    username: name,
    company: company,
    realm: 'LeapXpert'
  }

  return authenticateAPI
    .login(params, true)
    .then(async response => {
      params = {
        ...params,
        password,
        nonce: response.nonce,
        qop: response.qop,
        realm: response.realm
      }
      try {
        const secondResponse = await authenticateAPI.login(params, false)
        dispatch(
          loginSuccess(
            secondResponse.phoneNumber,
            secondResponse.ticket,
            secondResponse.userId,
            secondResponse.companyId
          )
        )
      } catch (err) {
        dispatch(loginFailure(name, password, company, err.toString()))
        localStorage.removeItem('session')
      }
    })
    .catch(error => {
      dispatch(loginFailure(name, password, company, error.toString()))
      localStorage.removeItem('session')
    })
}

export const loginMatrixSuccess = (
  matrixAccessToken: string,
  matrixId: string
) => {
  return {
    type: actionTypes.LOGIN_MATRIX_SUCCESS,
    payload: {
      matrixAccessToken,
      matrixId
    }
  }
}

export const loginSuccess = (
  phoneNumber: string,
  ticket: string,
  userId: string,
  companyId: string
) => {
  return {
    type: actionTypes.LOGIN_SUCCESS,
    payload: {
      phoneNumber: phoneNumber,
      ticket: ticket,
      userId: userId,
      companyId: companyId
    }
  }
}

export const loginFailure = (
  name: string,
  password: string,
  company: string,
  error: string
) => {
  return {
    type: actionTypes.LOGIN_FAILURE,
    payload: {
      name: name,
      password: password,
      company: company,
      error: error
    }
  }
}

export const verifyLogin = (
  ticket: string,
  passcode: string,
  userId: string
): ThunkAction<Action<any>> => (dispatch, getState) => {
  const companyId = getState().authentication.companyId || ''
  return actionCreator({
    actionType: actionTypes.VERIFY_LOGIN,
    apiFunction: authenticateAPI.verifyLogin,
    requestPayload: () => ({ticket, passcode, userId, companyId}),
    successPayload: response => ({
      matrixAccessToken: response.matrixAccessToken,
      matrixId: response.matrixId,
      accessToken: response.accessToken,
      userId: response.id,
      companyId: response.companyId,
      isAdmin: false
    }),
    taskAfterSuccess: response => {
      let session = JSON.parse(localStorage.getItem('session') || '{}')
      session = {
        ...session,
        accessToken: response.accessToken,
        userId: response.id,
        companyId: response.companyId,
        isAdmin: false,
        matrixAccessToken: response.matrixAccessToken,
        matrixId: response.matrixId
      }

      localStorage.setItem('session', JSON.stringify(session))
      bindDevice()(dispatch, getState)
    },
    taskAfterFailure: error => {
      localStorage.removeItem('session')
    }
  })(dispatch, getState)
}

export const bindDevice = (): ThunkAction<Action<any>> => async (
  dispatch,
  getState
) => {
  try {
    const keypair = await forge.rsa.generateKeyPair({bits: 2048, workers: -1})
    const privateKeyPem = forge.pki.privateKeyToPem(keypair.privateKey)
    const publicKeyPem = forge.pki.publicKeyToPem(keypair.publicKey)

    const device: DeviceInfo = {
      uniqueIdentifier: localStorage.getItem('deviceUniqueIdentifier') || '',
      hardwareIdentifier: navigator.appVersion,
      osIdentifier: navigator.platform,
      resolution: `${window.screen.width}x${window.screen.height}`,
      formFactor: 'DESKTOP',
      platform: 'WEB',
      publicEncryptionKey: publicKeyPem
    }
    return actionCreator({
      actionType: actionTypes.BIND_DEVICE,
      apiFunction: authenticateAPI.bindDevice,
      requestPayload: () => ({device}),
      successPayload: response => ({response}),
      taskAfterSuccess: response => {
        localStorage.setItem('privateKeyPem', privateKeyPem)
        localStorage.setItem('symmetricKeyV1', response.record.symmetricKeyV1)
        let session = JSON.parse(localStorage.getItem('session') || '{}')
        session = {
          ...session,
          accessToken: response.record.bondAccessToken
        }

        localStorage.setItem('session', JSON.stringify(session))
        dispatch(push(webURL.home))
      }
    })(dispatch, getState)
  } catch (err) {
    return dispatch({
      type: actionTypes.BIND_DEVICE_FAILURE,
      payload: {err}
    })
  }
}

export const fetchTokenFromStorage = (): Action<any> => {
  // OAEP-only key transport: a missing V1 key means the device is unbound.
  if (!localStorage.getItem('symmetricKeyV1')) {
    localStorage.removeItem('symmetricKey')
    localStorage.removeItem('privateKeyPem')
    localStorage.removeItem('session')
  }
  let session = JSON.parse(localStorage.getItem('session') || '{}')
  const token = {
    matrixAccessToken: session.matrixAccessToken,
    matrixId: session.matrixId,
    accessToken: session.accessToken,
    userId: session.userId,
    companyId: session.companyId,
    isAdmin: session.isAdmin
  }
  return {
    type: actionTypes.FETCH_TOKEN_FROM_STORAGE,
    payload: {...token}
  }
}

export const logout = (): ThunkAction<Action<any>> => async (
  dispatch,
  getState
) => {
  const logoutResponse = await authenticateAPI.logout()
  if (!logoutResponse.success) return
  localStorage.removeItem('session')
  localStorage.removeItem('privateKeyPem')
  localStorage.removeItem('symmetricKeyV1')
  localStorage.removeItem('symmetricKey')
  dispatch({type: actionTypes.LOGOUT, payload: null})
  dispatch(push(webURL.landing.loginAdmin))
}
